<template>
  <div
    id="root"
    :class="'theme-' + config.theme"
    class="text-theme min-h-screen flex flex-col"
  >

    <div v-wait:visible="'*'" id="saving-indicator">
      <div id="loading-bg-cover"></div>
      <div id="loading-spinner-wrap">
        <spring-spinner :animation-duration="2500" :size="64" :color="'#4fc6e1'"/>
      </div>
    </div>

    <template v-if="configStatus === 'complete' || (!IsLoggedIn && configStatus === 'error')">
      <notifications classes="notification-theme"/>

      <header-component v-if="IsLoggedIn"/>
      <div
        class="flex-grow w-full"
        :style="'padding-top:' + headerHeight + 'px'"
        :class="routeContainerType"
      >
        <left-menu v-show="isAdministrationRoute"/>
        <div
          :class="{
            'container-with-left-menu': isAdministrationRoute,
            'collapsed': collapsedLeftMenu
          }"
        >
          <p
            v-if="IsLoggedIn"
            class="text-theme light:text-theme-darker text-xl font-bold mt-3 py-1 uppercase tracking-wide"
            :class="{ 'pl-4' : !isAdministrationRoute }"
          >{{ $route.meta.title }}</p>
          <router-view class="rooter-view" :key="$route.fullPath"/>
        </div>
      </div>
      <footer-component v-if="IsLoggedIn"/>
    </template>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
const HeaderComponent = resolve => require(['@/components/layout/Header.vue'], resolve)
const FooterComponent = resolve => require(['@/components/layout/Footer.vue'], resolve)
import { SpringSpinner } from 'epic-spinners'
import RouterAuth from '@/router/auth'
const LeftMenu = resolve => require(['@/components/layout/LeftMenu'], resolve)

export default {
  components: {
    HeaderComponent,
    FooterComponent,
    LeftMenu,
    SpringSpinner,
  },
  data() {
    return {
      collapsedLeftMenu: false,
      windowWidth: null,
    }
  },
  watch: {
    'User.id'(id) {
      if (typeof id !== 'undefined' && this.config.status !== 'complete') {
        this.getConfig()
      }
    },
    $route() {
      this.setMenu()
    },
    AuthStatus(s) {
      if (s === 'error') {
        this.$router.push('/login')
      }
    },
    IsLoggedIn() {
      if (this.IsLoggedIn) {
        if (!this.Countries.length) {
          this['countries/getCountries']()
        }
      }
      if (this.config.status !== 'complete') {
        if (this.IsLoggedIn) {
          if (typeof this.User.id !== 'undefined') {
            this.getConfig()
          }
        } else if (this.config.status !== 'open-settings-error'){
          this['config/getOpen']()
          this['config/setComplete']()
        }
      }
    },
  },
  computed: {
    routeContainerType() {
      if (this.isAdministrationRoute) {
        return ''
      }

      let containerType = this.$route.meta.containerType
      if (containerType === 'boxed') {
        return 'container'
      }
      return 'container-fluid'
    },
    headerHeight() {
      if (this.isAdministrationRoute || this.windowWidth < this.config.screenBreakpoints.lg) {
        return 80
      }
      return 140
    },
    isAdministrationRoute() {
      if (!this.$route.name) {
        return false
      }
      return this.$route.name.split('.')[0] === 'administration'
    },
    ...mapGetters({
      Token: 'user/token',
      AuthStatus: 'user/authStatus',
      IsLoggedIn: 'user/isLoggedIn',
      Countries: 'countries/countries',
      User: 'user/user',
      isAdmin: 'user/isAdmin',
      UserPermissions: 'user/permissions',
      config: 'config/config',
      configStatus: 'config/status',
    })
  },
  created() {
    if (this.IsLoggedIn) {
      if (!this.Countries.length) {
        this['countries/getCountries']()
      }
      // get user if needed
      if (typeof this.User.id === 'undefined') {
        this['user/getUser']()
          .then(() => {
            RouterAuth.check(this.$route)
          })
      }
    }

    // get config
    if (this.config.status !== 'complete') {
      if (this.IsLoggedIn) {
        if (typeof this.User.id !== 'undefined') {
          this.getConfig()
        }
      } else {
        this['config/getOpen']()
        this['config/setComplete']()
      }
    }
  },
  mounted() {
    this.setMenu()
    this.$EventBus.$on('error:http', (error) => this.showHTTPError(error))
    this.$EventBus.$on('refreshConfig', this.getConfig)
    this.$EventBus.$on('collapsedLeftMenu', (collapsed) => this.collapsedLeftMenu = collapsed)

    window.addEventListener('resize', this.setWindowWidth)
    this.setWindowWidth()
  },
  destroyed() {
    this.$EventBus.$off('error:http')
    this.$EventBus.$off('refreshConfig')
    this.$EventBus.$off('collapsedLeftMenu')
    window.removeEventListener('resize', this.setWindowWidth)
  },
  methods: {
    async getConfig() {
      this['config/setIncomplete']()
      let requests = {
        config: await this['config/get'](),
        // all other config actions should go here.
      }

      if (this.UserPermissions && this.UserPermissions.filter(p => ['reminder.view', 'reminder.create', 'reminder.edit'].indexOf(p) !== -1).length) {
        requests['reminderMethods'] = await this['config/getReminderMethods']()
      }
      requests['financialAccounts'] = await this['config/getFinancialAccounts']()

      Promise.all(Object.values(requests))
        .then(() => {
          this['config/setComplete']()
        })
    },
    setWindowWidth() {
      this.windowWidth = window.innerWidth
    },
    setMenu() {
      let body = document.body
      if (this.isAdministrationRoute) {
        if (!body.classList.contains('left-menu')) {
          body.classList.remove('top-menu')
          body.classList.add('left-menu')
        }
      } else if (!body.classList.contains('top-menu')) {
        body.classList.remove('left-menu')
        body.classList.add('top-menu')
      }
    },
    showHTTPError(error) {
      let message = typeof error.data === 'string' ? error.data : ''
      if (error.status === 429) {
        message = 'Please try again in a while'
      }
      this.$swal.fire({
        title: error.status + ': ' + error.statusText,
        text: message,
        type: 'error',
      })
    },
    ...mapActions([
      'countries/getCountries',
      'user/getUser',
      'config/get',
      'config/getReminderMethods',
      'config/getOpen',
      'config/setComplete',
      'config/setIncomplete',
      'config/getFinancialAccounts',
    ])
  }
}
</script>
